/**
 * FERP Portal - Task Group List Page (DRR-125 Sprint 4)
 *
 * Config Type #5 (tg-): Task group templates defining ordered sets of
 * child task templates. Used to stamp out recurring care workflows.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: all data from backend taskGroups query
 * - No hardcoded field values — displays what backend returns
 * - Admin view layer only
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTaskGroupList } from '@/hooks/useTaskGroups';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Table,
  Paper,
  ThemeIcon,
  Alert,
  Button,
  Loader,
  TextInput,
  Select,
} from '@mantine/core';
import {
  IconListCheck,
  IconAlertTriangle,
  IconRefresh,
  IconSearch,
  IconShieldFilled,
} from '@tabler/icons-react';
import { TaskGroupCategory } from '@/generated/graphql';

import type { TaskGroupSummary } from '@/hooks/useTaskGroups';

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: TaskGroupCategory.ADL, label: 'ADL' },
  { value: TaskGroupCategory.CLINICAL, label: 'Clinical' },
  { value: TaskGroupCategory.MEDICATION, label: 'Medication' },
  { value: TaskGroupCategory.NUTRITION, label: 'Nutrition' },
  { value: TaskGroupCategory.SAFETY, label: 'Safety' },
  { value: TaskGroupCategory.SOCIAL, label: 'Social' },
  // Standalone task group categories (not yet in generated enum)
  { value: 'housekeeping', label: 'Housekeeping' },
  { value: 'admission', label: 'Admission' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'ledger', label: 'Ledger' },
];

export function TaskGroupListPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  const { taskGroups, loading, error, refetch } = useTaskGroupList({
    category: selectedCategory || undefined,
  });

  const filtered = useMemo(() => {
    if (!searchQuery) return taskGroups;
    const q = searchQuery.toLowerCase();
    return taskGroups.filter(
      (tg) =>
        tg.name.toLowerCase().includes(q) ||
        tg.configId.toLowerCase().includes(q) ||
        tg.category.toLowerCase().includes(q) ||
        tg.setting.toLowerCase().includes(q) ||
        tg.subgraph.toLowerCase().includes(q),
    );
  }, [taskGroups, searchQuery]);

  const lifeCriticalCount = useMemo(
    () => taskGroups.filter((tg) => tg.isLifeCritical).length,
    [taskGroups],
  );

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Task Groups">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Task Groups</Title>
            <Text c="dimmed" size="sm">
              Config type tg- — ordered sets of child task templates
            </Text>
          </div>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>

        {/* Stats */}
        <Group gap="md">
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm">
                <IconListCheck size={14} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Total</Text>
                <Text size="xl" fw={700}>{taskGroups.length}</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="red">
                <IconShieldFilled size={14} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Life-Critical</Text>
                <Text size="xl" fw={700}>{lifeCriticalCount}</Text>
              </div>
            </Group>
          </Card>
        </Group>

        {/* Filters */}
        <Group gap="md">
          <TextInput
            placeholder="Search task groups..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Filter by category"
            data={CATEGORY_OPTIONS}
            value={selectedCategory}
            onChange={(v) => setSelectedCategory(v ?? '')}
            clearable
            style={{ width: 200 }}
          />
        </Group>

        {/* Table */}
        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Task Group</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Setting</Table.Th>
                  <Table.Th>Subgraph</Table.Th>
                  <Table.Th>Tasks</Table.Th>
                  <Table.Th>Completion</Table.Th>
                  <Table.Th>Flags</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text ta="center" c="dimmed" py="md">
                        No task groups found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  filtered.map((tg) => (
                    <Table.Tr
                      key={tg.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/ferp-core/task-groups/${tg.configId}`)}
                    >
                      <Table.Td>
                        <Stack gap={0}>
                          <Text size="sm" fw={500} lineClamp={1}>{tg.name}</Text>
                          <Text size="xs" c="dimmed">{tg.configId}</Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" size="sm" color={tg.color ?? 'blue'}>
                          {tg.category}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{tg.setting}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="outline" size="sm">{tg.subgraph}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>{tg.children.length}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" size="sm" color="gray">
                          {tg.completionStrategy}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {tg.isLifeCritical && (
                          <Badge color="red" size="sm" variant="filled">
                            LIFE-CRITICAL
                          </Badge>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

export default TaskGroupListPage;
